import { useEffect, useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AddResource from "../../../components/header/AddResource";
import MainContainer from "../../../components/container/MainContainer";
import { getCommunity } from "../../../features/communitiesSlice";
import InputField from "../../../components/form/InputField";
import {
  addBeneficiaryProfile,
  getProject,
} from "../../../features/projectsSlice";
import useGeolocation from "../../../hooks/useGeolocation";
import PhoneInputField from "../../../components/form/PhoneInputField";
import { zodResolver } from "@hookform/resolvers/zod";
import { addBeneficiaryProfileSchema } from "../../../schemas";
import Loading from "../../../components/loading/Loading";
import { useNotification } from "../../../components/notification/NotificationContainer";
import DisplayComments from "../../../components/text/DisplayComments";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";

const AddBeneficiaryProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loc = useLocation();
  const community = useSelector((state) => state.communities.selected);
  const user = useSelector((state) => state.user.userInfo);
  const project = useSelector((state) => state.projects.selected);

  const getProjectLoad = useSelector((state) => state.projects.load.getProject);
  const getCommunityLoad = useSelector(
    (state) => state.communities.load.getCommunity
  );
  const addBeneficiaryLoad = useSelector(
    (state) => state.projects.load.addBeneficiaryProfile
  );

  const [isMobile, setIsMobile] = useState(false); // State to track if device is mobile
  const { error, getLocation, loading, location, stopWatching } =
    useGeolocation({}, 100);
  const {
    register,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(addBeneficiaryProfileSchema) });
  const addNotification = useNotification();
  const { id } = useParams();

  const onSubmit = async (data, e) => {
    try {
      const res = await dispatch(addBeneficiaryProfile(data));
      if (addBeneficiaryProfile.fulfilled.match(res)) {
        if (id) addNotification("Project Saved successfully!", "success", 3000);
        else addNotification("Project created successfully!", "success", 3000);
        return navigate("/requests/problem-statement/" + res.payload.id);
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.message, "error");
    }
  };

  useLayoutEffect(() => {
    if (id && !project.id) {
      dispatch(getProject(id));
    }
  }, []);

  useEffect(() => {
    if (user.id && user.communities.length > 0) {
      dispatch(getCommunity(user.communities[0].id));
    }
  }, [user]);

  useEffect(() => {
    if (user.id) {
      reset({ created_by: user.id, phone: user.phone });
      if (community.id) {
        reset({ community_id: community.id });
      }

      if (id) {
        reset({
          id: project.id,
          community_id: project.community_id,
          project_location: project.project_location,
          longitude: project.longitude,
          latitude: project.latitude,
          title: project.name,
          phone: project.creator?.phone,
        });
      }
    }
  }, [user, project]);

  useLayoutEffect(() => {
    if (
      (id && project.id && project.stage.step_number < 1) ||
      (id && project.id && project.stage.step_number > 3)
    ) {
      return navigate("/requests/my-requests");
    }
  }, [project]);

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android|iphone|ipad|ipod|windows phone/i.test(userAgent)) {
      setIsMobile(true); // Mobile device
    } else {
      setIsMobile(false); // Not a mobile device
    }
  }, []);

  if (
    (id && !project.id) ||
    getProjectLoad === loadStatus.loading ||
    getCommunityLoad === loadStatus.loading
  ) {
    return <Loading />;
  }

  if (
    getProjectLoad === loadStatus.error ||
    getCommunityLoad === loadStatus.error
  ) {
    return <NetworkError to={loc.pathname} />;
  }

  return (
    <MainContainer>
      <div className="w-full max-w-6xl mx-auto">
        <AddResource
          title={
            id
              ? "Edit Project Request - Need Assessment"
              : "Project Request - Need Assessment"
          }
        />
        {user.id && user.communities.length <= 0 ? (
          <div className="h-72 sm:h-80 lg:h-96 border border-bkg2 rounded-md text-text-1/60 flex items-center justify-center">
            <p className="text-center">
              Your don't belong to any Community, Please contact your
              Administrator
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {user.id && community.id && (
              <>
                <div className="text-text-1/70  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 font-roboto">
                  <h5 className="text-text-1/80 ">Beneficiary Profile</h5>
                  <p className="summary-p">
                    Name of Community:
                    <spam className="summary-span">{community.name}</spam>
                  </p>
                  <p className="summary-p">
                    Address:
                    <spam className="summary-span">
                      {community.address1 + community.address2}
                    </spam>
                  </p>
                  <p className="summary-p">
                    Province:
                    <spam className="summary-span">{user.province.name}</spam>
                  </p>
                  <p className="summary-p">
                    Country:
                    <spam className="summary-span">{community.country}</spam>
                  </p>
                  <p className="summary-p">
                    Email :<spam className="summary-span">{user.email}</spam>
                  </p>
                  <p className="summary-p">
                    Contact:
                    <spam className="summary-span">
                      {user.phone || "----------"}
                    </spam>
                  </p>
                </div>
                {/* Comments */}
                {project.id && (
                  <DisplayComments comments={project.comments || []} />
                )}

                <div className="text-text-1/60 border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 bg-content">
                  <div className="sm:flex">
                    <PhoneInputField
                      register={register}
                      setValue={setValue}
                      name="phone"
                      label="Phone Number"
                      defaultValue={
                        id
                          ? project.creator?.phone
                          : `${user.phone ? user.phone : "---------"}`
                      }
                      errors={errors.phone}
                    />
                    <div className="flex-1"></div>
                  </div>
                  <div className="sm:flex">
                    <InputField
                      register={register}
                      name="project_location"
                      label="Project Location"
                      errors={errors.project_location}
                    />
                    <div className="flex-1"></div>
                  </div>
                  <InputField
                    register={register}
                    name="title"
                    label="Proposed Title"
                    errors={errors.title}
                    type="text"
                  />
                  <div className="flex">
                    <InputField
                      register={register}
                      name="community_id"
                      defaultValue={community.id}
                      type="hidden"
                    />
                    <InputField
                      register={register}
                      name="created_by"
                      defaultValue={user.id}
                      type="hidden"
                    />
                    <InputField
                      register={register}
                      name="longitude"
                      defaultValue={null}
                      type="hidden"
                    />
                    <InputField
                      register={register}
                      name="latitude"
                      defaultValue={null}
                      type="hidden"
                    />
                    <InputField
                      register={register}
                      name="province_id"
                      defaultValue={user.province.id}
                      type="hidden"
                    />
                    <InputField
                      register={register}
                      name="id"
                      defaultValue={null}
                      type="hidden"
                    />
                    <InputField
                      register={register}
                      name="phone"
                      type="hidden"
                    />
                  </div>
                  <button
                    className="bg-primary text-text-gen/80 px-4 py-2 rounded mb-4 sm:hidden"
                    onClick={() => getLocation(true)} // Get location with high accuracy
                    disabled={loading}
                  >
                    {loading ? "Getting Location..." : "Get Location"}
                  </button>

                  {/* Buttons */}
                  <div className="flex justify-between items-center pt-4 pb-10">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/requests/my-requests");
                      }}
                      className="cancel-rss-btn"
                      disabled={addBeneficiaryLoad}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={addBeneficiaryLoad}
                      className="add-rss-btn"
                    >
                      Save & Continue
                    </button>
                  </div>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </MainContainer>
  );
};

export default AddBeneficiaryProfile;
